import React, { useEffect, useState } from 'react';
import { pageTitle } from '../../helper';
import Accordion from '../Accordion';
import Button from '../Button';
import Cta from '../Cta';
import PageHeading from '../PageHeading';
import PricingTable from '../PricingTable';
import Div from '../Div';
import SectionHeading from '../SectionHeading';
import TestimonialSlider from '../Slider/TestimonialSlider';
import Spacing from '../Spacing';

export default function Office365Page() {
    pageTitle('Office 365 Management');

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const [tab, setTab] = useState('monthly');
    const [vatTab, setVatTab] = useState('excl');
    const [includeVAT, setIncludeVAT] = useState(false); // VAT toggle state

    // Function to calculate VAT-inclusive price
    const calculatePriceWithVAT = (price) => {
        return includeVAT ? (price * 1.21).toFixed(2) : price.toFixed(2);
    };

    return (
        <>
            {/* Hero Section */}
            <PageHeading
                title='Office 365 Management'
                bgSrc='/images/service_hero_bg.jpeg'
                pageLinkText='Office 365 Management'
            />
            <Spacing lg='145' md='80'/>

            {/* Service Overview */}
            <Div className="container cs-light_bg">
                <SectionHeading
                    title='Seamless Office 365 Management for Your Business'
                    subtitle='Fully Managed Microsoft 365 Services'
                    variant='cs-style1 text-center'
                />
                <Spacing lg='50' md='30'/>
                <p className="text-center cs-font_18">
                    Microsoft 365 is a powerful suite of cloud-based tools for business productivity, but managing it effectively
                    requires expertise. At <b>ACE</b>, we provide secure, scalable, and fully managed Office 365 solutions, allowing
                    you to focus on running your business while we handle the technical details.
                </p>
            </Div>

            <Spacing lg='90' md='45'/>


            <Spacing lg='50' md='30'/>

            {/* Pricing Section */}
            <Div className="container cs-dark_bg">
                <SectionHeading
                    title='Microsoft 365 Plans & Pricing'
                    subtitle='Flexible Plans for Businesses of All Sizes'
                />
                <Spacing lg='50' md='30'/>

                <Div className="position-relative">
                    <ul className="cs-tab_links cs-style1 cs-mp0 cs-primary_font text-center">
                        <li className={tab === 'monthly' ? "active" : ""} onClick={()=>setTab('monthly')}>Monthly</li>
                        <li className={tab === 'yearly' ? "active" : ""} onClick={()=>setTab('yearly')}>Yearly</li>
                    </ul>
                    {/* VAT Toggle */}
                    <Div className="container text-center">
                        <label className="cs-font_18">
                            <input
                                type="checkbox"
                                checked={includeVAT}
                                onChange={() => setIncludeVAT(!includeVAT)}
                            />
                            {' '} Show prices including VAT (21%)
                        </label>
                    </Div>
                    <Div className="row">
                        <Div className="col-lg-4">
                            {tab === 'monthly' && (
                                <PricingTable
                                    title='Business Basic'
                                    price={calculatePriceWithVAT(9.99)}
                                    currency='€'
                                    timeline='monthly per user'
                                    features={[
                                        'Microsoft Teams, Outlook, Word, Excel, PowerPoint (Web & Mobile)',
                                        '1 TB OneDrive Storage',
                                        'Standard Security & Compliance',
                                        'Managed Setup & Admin Support',
                                        'Basic Technical Support'
                                    ]}
                                    btnText='Subscribe Now'
                                    btnLink='https://buy.stripe.com/fZedT345s3iS6sw9AA'
                                    vatIncluded={includeVAT}
                                />
                            )}
                            {tab === 'yearly' && (
                                <PricingTable
                                    title='Business Basic'
                                    price={calculatePriceWithVAT(119.88)}
                                    currency='€'
                                    timeline='yearly per user'
                                    features={[
                                        'Microsoft Teams, Outlook, Word, Excel, PowerPoint (Web & Mobile)',
                                        '1 TB OneDrive Storage',
                                        'Standard Security & Compliance',
                                        'Managed Setup & Admin Support',
                                        'Basic Technical Support'
                                    ]}
                                    btnText='Subscribe Now'
                                    btnLink='https://buy.stripe.com/fZe6qBdG2aLk18cdQR'
                                    vatIncluded={includeVAT}
                                />
                            )}
                            <Spacing lg='25' md='25'/>
                        </Div>

                        <Div className="col-lg-4">
                            {tab === 'monthly' && (
                                <PricingTable
                                    title='Business Standard'
                                    price={calculatePriceWithVAT(16.99)}
                                    currency='€'
                                    timeline='monthly per user'
                                    features={[
                                        'Desktop versions of Office apps',
                                        'Business-class email hosting (50GB Mailbox)',
                                        'Microsoft Teams, SharePoint & OneDrive',
                                        'Advanced Security & Compliance',
                                        'Admin & User Support'
                                    ]}
                                    btnText='Subscribe Now'
                                    btnLink='https://buy.stripe.com/5kAaGRatQ6v44kodQS'
                                    vatIncluded={includeVAT}
                                />
                            )}
                            {tab === 'yearly' && (
                                <PricingTable
                                    title='Business Standard'
                                    price={calculatePriceWithVAT(203.88)}
                                    currency='€'
                                    timeline='yearly per user'
                                    features={[
                                        'Desktop versions of Office apps',
                                        'Business-class email hosting (50GB Mailbox)',
                                        'Microsoft Teams, SharePoint & OneDrive',
                                        'Advanced Security & Compliance',
                                        'Admin & User Support'
                                    ]}
                                    btnText='Subscribe Now'
                                    btnLink='https://buy.stripe.com/28o5mxcBY8DcaIMeUX'
                                    vatIncluded={includeVAT}
                                />
                            )}
                            <Spacing lg='25' md='25'/>
                        </Div>

                        <Div className="col-lg-4">
                            {tab === 'monthly' && (
                                <PricingTable
                                    title='Business Premium'
                                    price={calculatePriceWithVAT(29.99)}
                                    currency='€'
                                    timeline='monthly per user'
                                    features={[
                                        'Everything in Business Standard',
                                        'Microsoft Defender for Office 365',
                                        'Advanced Threat Protection & Data Loss Prevention',
                                        '24/7 Security Monitoring & Incident Response',
                                        'Dedicated Account Manager'
                                    ]}
                                    btnText='Subscribe Now'
                                    btnLink='https://buy.stripe.com/8wMbKVdG22eO3gkeUY'
                                    vatIncluded={includeVAT}
                                />
                            )}
                            {tab === 'yearly' && (
                                <PricingTable
                                    title='Business Premium'
                                    price={calculatePriceWithVAT(359.88)}
                                    currency='€'
                                    timeline='yearly per user'
                                    features={[
                                        'Everything in Business Standard',
                                        'Microsoft Defender for Office 365',
                                        'Advanced Threat Protection & Data Loss Prevention',
                                        '24/7 Security Monitoring & Incident Response',
                                        'Dedicated Account Manager'
                                    ]}
                                    btnText='Subscribe Now'
                                    btnLink='https://buy.stripe.com/3cscOZ0Tg7z84ko5kp'
                                    vatIncluded={includeVAT}
                                />
                            )}
                            <Spacing lg='25' md='25'/>
                        </Div>
                    </Div>
                </Div>
            </Div>

            <Spacing lg='120' md='50'/>

            {/* Call to Action */}
            <Div className="container">
                <Cta
                    title='Secure & Streamline Your Business with Office 365'
                    btnText='Get a Consultation'
                    btnLink='/contact'
                    bgSrc='/images/cta_bg.jpeg'
                />
            </Div>
        </>
    );
}
